<template>
    <v-combobox v-show="value"
        v-model="selection_calc"
        :items="available_items"
        chips
        clearable
        :label="label"
        :hint="label"
        multiple
        solo
        flat
        outlined
        dense
        persistent-hint
    >
        <template v-slot:selection="{ attrs, item, select, selected }">
            <v-chip
                v-bind="attrs"
                :input-value="selected"
                close
                @click="select"
                class="my-1"
                @click:close="remove(item)"
            >
                <strong>{{ get_significant_name(item) }}</strong>&nbsp;
                <span></span>
            </v-chip>
        </template>
    </v-combobox>
</template>

<script>
export default {
    name: "ChipSelect",
    props: {
        'value': {
            // The visibility of this panel.  To be used with `v-model`
            type: Boolean,
            required: true
        },
        'label': {
            // The label that will show up on the combo box
            type: String,
            required: false,
            default: function () {
                return null
            }
        },
        'valuelist': {
            // These are the selectable items.  All of them.  When selected they will show up as chips.  When not selected, they will be in the combobox
            type: Array,
            required: false,
            default: function () {
                return []
            },
        },
        'keyname': {
            // If the elements of your valuelist are objects, and they don't have standard IDs like `value` or `id`, then you can specify the id column's name here
            type: String,
            required: false,
            default: function () {
                return undefined
            },
        },
        'labelname': {
            // If the elements of your valuelist are objects, and they don't have standard text label properties like `text` or `name`, then you can specify the label column's name here
            type: String,
            required: false,
            default: function () {
                return undefined
            },
        },
        'selection': {
            // An array of the `id`s from your valuelist that have already been selected
            type: Array,
            required: false,
            default: function () {
                return []
            },
        },

    },
    data () {
        return {
            // selection_local: this.selection || ['Streaming', 'Eating'],
        }
    },
    computed: {
        selected_items() {
            // This is the valuelist, but filtered to show only the items that are currently selected
            return this.valuelist.filter(valuelistitem => {
                let this_data_point = this.get_significant_id(valuelistitem)
                return this.selection_calc && this.selection_calc.includes(this_data_point)
            })
        },
        available_items() {
            // This is the valuelist, but filtered to show only the items that are currently *not* selected
            return this.valuelist.filter(valuelistitem => {
                let this_data_point = this.get_significant_id(valuelistitem)
                return !this.selection_calc || !this.selection_calc.includes(this_data_point)
            })
                .map(valuelistitem => {
                return {
                    text: this.get_significant_name(valuelistitem),
                    value: this.get_significant_id(valuelistitem)
                }
            })
        },
        selection_calc: {
            // The getter will simply show the selection as passed-in via prop
            get() {
                return this.selection || []
            },
            // The setter will massage the array to return only the ids from the selected valuelist items
            set(newValue) {
                if (newValue && newValue.length) {
                    this.$emit('update:selection', this.get_ids_array(newValue))
                } else {
                    this.$emit('update:selection', [])
                }
            }
        },
    },

    methods: {
        remove (item) {
            // Deselects a previously selected item
            let match_from_selection = this.selection_calc.findIndex(selected_item => this.get_significant_id(selected_item) === this.get_significant_id(item))
            if (match_from_selection > -1) {
                this.selection_calc.splice(match_from_selection, 1)
                // Update the parent so that it knows the selection has been updated
                this.$emit('update:selection', this.selection_calc)
            } else {
                // console.log("No match was found")
            }
        },
        is_primitive(value) {
            // A quick way of determining if the input is primitive
            return value === null || !(["object", "function"].includes(typeof value))
        },
        get_significant_id(data) {
            // This will hopefully always find the important data.
            // If a primitive is passed in, that's what will come out too
            if (this.is_primitive(data)) {
                return data
            }
            // If a specific keyname was asked for, then that's what we'll use
            let named_attribute = data[this.keyname] || null
            return this.keyname ?
                named_attribute :
                (data.value || data.id || null)
            // in all other cases, we'll check the input for a value or id prop
        },
        get_significant_name(data) {
            // This will hopefully always find the important data.
            if (this.is_primitive(data)) {
                // If valuelist consists of non-primitive elements, then we should take that into consideration.  This primitive could be a key
                let valuelist_uses_primitives = (this.valuelist && this.valuelist.length) ? this.is_primitive(this.valuelist[0]) : true
                if (valuelist_uses_primitives) {
                    return data
                }
                let match_by_id = this.valuelist.findIndex(valuelistitem => this.get_significant_id(valuelistitem) === data)
                return match_by_id > -1 ? this.get_significant_name(this.valuelist[match_by_id]) : data
            }
            // If a specific labelname was asked for, then that's what we'll use
            let named_attribute = data[this.labelname] || null
            return this.labelname ?
                named_attribute :
                (data.text || data.name || null)
            // in all other cases, we'll check the input for a text or name prop
        },
        get_ids_array(input) {
            // This will simplify an array so that it will show only significant ids
            return input.reduce(
                (prev, curr) => {
                    let my_id = this.get_significant_id(curr)
                    prev.push(my_id)
                    return prev
                },
                []
            )
        },
        get_ids_array_from_selection() {
            return this.get_ids_array(this.selection)
        }
    },
}
</script>

<style lang="sass">


</style>
